import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { User } from '../../shared/api';
import { api, ApiError, errorMessage, isAbortError, onUnauthenticated } from '../lib/api';

export type AuthState =
  | { status: 'loading' }
  | { status: 'signed-out' }
  | { status: 'signed-in'; user: User }
  | { status: 'error'; message: string };

interface AuthContextValue {
  state: AuthState;
  retry: () => void;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    api.me(controller.signal).then(
      (user) => setState({ status: 'signed-in', user }),
      (error: unknown) => {
        if (isAbortError(error)) return;
        if (error instanceof ApiError && error.status === 401) setState({ status: 'signed-out' });
        else setState({ status: 'error', message: errorMessage(error) });
      },
    );
    return () => controller.abort();
  }, [attempt]);

  // Any API call that finds the session gone (expired, or signed out elsewhere) returns to sign-in.
  useEffect(() => onUnauthenticated(() => setState({ status: 'signed-out' })), []);

  const retry = useCallback(() => {
    setState({ status: 'loading' });
    setAttempt((n) => n + 1);
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      setState({ status: 'signed-out' });
    }
  }, []);

  const value = useMemo(() => ({ state, retry, signOut }), [state, retry, signOut]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** The signed-in user; only for components rendered after sign-in. */
export function useCurrentUser(): User {
  const { state } = useAuth();
  if (state.status !== 'signed-in') throw new Error('useCurrentUser() used while signed out');
  return state.user;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside <AuthProvider>');
  return value;
}
